import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { visuallyHidden } from '@mui/utils';
import { Button } from "@mui/material"
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getCurrentLangCode } from '../utils/language';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCellsDe = [
  {
    id: 'produkt',
    numeric: false,
    disablePadding: true,
    label: 'Produkt',
  },
  {
    id: 'isin',
    numeric: false,
    disablePadding: false,
    label: 'ISIN',
  },
  {
    id: 'datum',
    numeric: false,
    disablePadding: false,
    label: 'Datum',
  },
  {
    id: 'uhrzeit',
    numeric: false,
    disablePadding: false,
    label: 'Uhrzeit',
  },
  {
    id: 'anzahl',
    numeric: true,
    disablePadding: false,
    label: 'Anzahl',
  },
  {
    id: 'kurs',
    numeric: true,
    disablePadding: false,
    label: 'Kurs',
  },
  {
    id: 'transaktionsgebuehren',
    numeric: false,
    disablePadding: false,
    label: 'Transaktionsgebühr',
  },
  {
    id: 'gesamt',
    numeric: true,
    disablePadding: false,
    label: 'Gesamt',
  },


];

const headCellsEn = [
  {
    id: 'produkt',
    numeric: false,
    disablePadding: true,
    label: 'Product',
  },
  {
    id: 'isin',
    numeric: false,
    disablePadding: false,
    label: 'ISIN',
  },
  {
    id: 'datum',
    numeric: false,
    disablePadding: false,
    label: 'Date',
  },
  {
    id: 'uhrzeit',
    numeric: false,
    disablePadding: false,
    label: 'Time',
  },
  {
    id: 'anzahl',
    numeric: true,
    disablePadding: false,
    label: 'Quantity',
  },
  {
    id: 'kurs',
    numeric: true,
    disablePadding: false,
    label: 'Cost',
  },
  {
    id: 'transaktionsgebuehren',
    numeric: false,
    disablePadding: false,
    label: 'Transaction fee',
  },
  {
    id: 'gesamt',
    numeric: true,
    disablePadding: false,
    label: 'Total',
  },
];

const headCellsPl = [
  { id: 'produkt', numeric: false, disablePadding: true, label: 'Produkt' },
  { id: 'isin', numeric: false, disablePadding: false, label: 'ISIN' },
  { id: 'datum', numeric: false, disablePadding: false, label: 'Data' },
  { id: 'uhrzeit', numeric: false, disablePadding: false, label: 'Godzina' },
  { id: 'anzahl', numeric: true, disablePadding: false, label: 'Ilość' },
  { id: 'kurs', numeric: true, disablePadding: false, label: 'Koszt' },
  { id: 'transaktionsgebuehren', numeric: false, disablePadding: false, label: 'Opłata transakcyjna' },
  { id: 'gesamt', numeric: true, disablePadding: false, label: 'Suma' },
];

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
    props;
  const headCells = props.headCells || headCellsDe;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar(props) {
  const { numSelected, onDelete, isPolish, isGerman } = props;
  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        },
        numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        },
      ]}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} {isPolish ? 'zaznaczono' : (isGerman ? 'ausgewählt' : 'selected')}
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {isPolish ? 'Rekordy' : (isGerman ? 'Datensätze' : 'Records')}
        </Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title={isPolish ? 'Usuń' : (isGerman ? 'Entfernen' : 'Delete')}>
          <IconButton onClick={(e) => { e.stopPropagation(); if (onDelete) onDelete(); }}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title={isPolish ? 'Filtruj listę' : (isGerman ? 'Liste filtern' : 'Filter list')}>
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onDelete: PropTypes.func,
};


export default function Preview({orderBooks, setOrderBooks, setCurrentStep, selected, setSelected, isGerman = true}) {
  const isPolish = getCurrentLangCode() === 'pl';
  const headCells = isPolish ? headCellsPl : (isGerman ? headCellsDe : headCellsEn);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('produkt'); // Default sort by product
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    function hideDenseToggle() {
      try {
        const nodes = document.querySelectorAll('label, .MuiFormControlLabel-root, [role="switch"]');
        for (let i = 0; i < nodes.length; i++) {
          const el = nodes[i];
          const text = (el.textContent || '').trim().toLowerCase();
          if (text.includes('dense padding') || text.includes('kompakte ansicht')) {
            const wrapper =
              el.closest('label') ||
              el.closest('.MuiFormControlLabel-root') ||
              el;
            if (wrapper) wrapper.style.display = 'none';
          }
        }
      } catch (e) {}
    }

    hideDenseToggle();
    const t1 = setTimeout(hideDenseToggle, 300);
    const t2 = setTimeout(hideDenseToggle, 1000);
    const iv = setInterval(hideDenseToggle, 2000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearInterval(iv);
    };
  }, []);

  //selects all as standart
  useEffect(() => {
    const allIds = orderBooks.map((row) => row.id);
    setSelected(allIds)
  }, [orderBooks]);   // is allweis executet when orderBooks is changed 

  const handleDeleteSelected = () => {
    if (setOrderBooks) {
      const remaining = orderBooks.filter((row) => !selected.includes(row.id));
      setOrderBooks(remaining);
      setSelected([]);
      setPage(0);
      // If all rows deleted, go back to upload
      if (remaining.length === 0) {
        setCurrentStep({ activeStep: 0, status: "back-from-preview" });
      }
    }
  };


  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = orderBooks.map((n) => n.id); // Use orderBooks for selection
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - orderBooks.length) : 0;

  const paginationRowsPerPageLabel = isPolish
    ? 'Wierszy na stronę:'
    : (isGerman ? 'Zeilen pro Seite:' : 'Rows per page:');

  const paginationDisplayedRowsLabel = ({ from, to, count }) => {
    if (isPolish) {
      return `${from}-${to} z ${count !== -1 ? count : `więcej niż ${to}`}`;
    }
    if (isGerman) {
      return `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`;
    }
    return `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`;
  };

  const paginationAriaLabel = (type) => {
    if (isPolish) {
      if (type === 'first') return 'Pierwsza strona';
      if (type === 'last') return 'Ostatnia strona';
      if (type === 'next') return 'Następna strona';
      return 'Poprzednia strona';
    }
    if (isGerman) {
      if (type === 'first') return 'Erste Seite';
      if (type === 'last') return 'Letzte Seite';
      if (type === 'next') return 'Nächste Seite';
      return 'Vorherige Seite';
    }
    if (type === 'first') return 'First page';
    if (type === 'last') return 'Last page';
    if (type === 'next') return 'Next page';
    return 'Previous page';
  };

  const visibleRows = useMemo(
    () =>
      [...orderBooks]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, orderBooks],
  );

  const isItemSelected = (id) => selected.includes(id);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <EnhancedTableToolbar numSelected={selected.length} onDelete={handleDeleteSelected} isPolish={isPolish} isGerman={isGerman} />
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size="medium"
          >
            <EnhancedTableHead
              headCells={headCells}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={orderBooks.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                const itemSelected = isItemSelected(row.id);

                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, row.id)}
                    role="checkbox"
                    aria-checked={itemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={itemSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={itemSelected}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="none"
                    >
                      {row.produkt}
                    </TableCell>
                    <TableCell>{row.isin}</TableCell>
                    <TableCell>{row.datum}</TableCell>
                    <TableCell>{row.uhrzeit}</TableCell>
                    <TableCell align="right">{row.anzahl}</TableCell>
                    <TableCell align="right">{row.kurs}</TableCell>
                    <TableCell align="right">{row.transaktionsgebuehren}</TableCell>
                    <TableCell align="right">{row.gesamt}</TableCell>
                  </TableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={headCells.length + 1} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={orderBooks.length} // Use orderBooks length
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={paginationRowsPerPageLabel}
          labelDisplayedRows={paginationDisplayedRowsLabel}
          getItemAriaLabel={paginationAriaLabel}
        />
      </Paper>
      <div className="flex justify-between mt-10">
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
          onClick={() => { setCurrentStep({ activeStep: 0, status: "back-from-preview" }); }}
          variant="text"
        >
          {isPolish ? 'Wstecz' : (isGerman ? 'Zurück' : 'Back')}
        </Button>
        <Button
          sx={{ textTransform: 'none' }}
          onClick={() => { setCurrentStep({ activeStep: 2, status: "valid" }); }}
          variant="outlined"
        >
          {isPolish ? 'Potwierdź' : (isGerman ? 'Bestätigung' : 'Confirm')}
        </Button>
      </div>
    </Box>
  );
}